import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import {
  ARIA_ESCALATION_QUEUE_MAP,
  ARIA_ESCALATION_STATUS_TABS,
  ARIA_HANDOFF_STATUS_META,
} from '../ariaHandsoffConstants';
import {
  ariaAvatarFallbackSx,
  ariaColumnDividerSx,
  ariaListItemHoverSx,
  ariaSegmentTabSx,
  ariaSegmentTrackSx,
  ariaSelectedListItemSx,
} from '../ariaHandsoffSurfaces';
import { AriaChatListSkeleton } from './AriaSkeletons';
import { formatDate, MOMENT_FORMATS, toMoment } from '../../../utils/dateFormat';

const formatListTime = (value) => {
  const date = toMoment(value);
  if (!date) return '';

  if (date.isSame(toMoment(new Date()), 'day')) {
    return formatDate(date, MOMENT_FORMATS.TIME) || '';
  }

  return formatDate(date, MOMENT_FORMATS.DATE_SHORT) || '';
};

const AriaChatList = ({
  chats = [],
  selectedChatId,
  queueLabel,
  activeStatus,
  isLoading = false,
  onSelectChat,
  onStatusChange,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        ...ariaColumnDividerSx,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 0, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary' }}>
            {queueLabel || 'Conversations'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{ ...ariaSegmentTrackSx, px: 1, flexShrink: 0 }}
        role="tablist"
        aria-label="Escalation status"
      >
        {ARIA_ESCALATION_STATUS_TABS.map((tab) => {
          const selected = tab.value === activeStatus;
          return (
            <Box
              key={tab.value}
              component="button"
              type="button"
              role="tab"
              aria-selected={selected}
              disabled={isLoading}
              onClick={() => onStatusChange?.(tab.value)}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: isLoading ? 'default' : 'pointer',
                px: 0.5,
                py: 1,
                fontSize: '0.75rem',
                fontWeight: selected ? 700 : 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
                transition:
                  'color 120ms ease, border-color 120ms ease, background-color 120ms ease',
                ...ariaSegmentTabSx(selected, theme),
                '&:disabled': {
                  opacity: 0.7,
                },
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {isLoading ? (
          <AriaChatListSkeleton />
        ) : (
          <List disablePadding sx={{ px: 1, pb: 1 }}>
            {chats.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4, px: 2, fontWeight: 500, fontSize: '0.8125rem' }}
              >
                No conversations in this queue.
              </Typography>
            ) : null}

            {chats.map((chat) => {
              const selected = chat.id === selectedChatId;
              const statusMeta = ARIA_HANDOFF_STATUS_META[chat.status];
              const queueMeta = ARIA_ESCALATION_QUEUE_MAP[chat.queue];

              return (
                <ListItemButton
                  key={chat.id}
                  selected={selected}
                  onClick={() => onSelectChat?.(chat.id)}
                  sx={{
                    borderRadius: '10px',
                    mb: 0.5,
                    alignItems: 'flex-start',
                    py: 1,
                    px: 1.25,
                    ...(selected ? ariaSelectedListItemSx(theme) : ariaListItemHoverSx),
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44, mt: 0.25 }}>
                    <Badge
                      color="error"
                      badgeContent={chat.unreadCount || 0}
                      invisible={!chat.unreadCount}
                      overlap="circular"
                    >
                      <Avatar
                        src={chat.avatarUrl}
                        alt={chat.userName}
                        sx={{
                          width: 34,
                          height: 34,
                          fontSize: '0.8125rem',
                          ...ariaAvatarFallbackSx,
                        }}
                      >
                        {chat.userName?.charAt(0)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 0.25,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: chat.unreadCount ? 700 : 600,
                            fontSize: '0.8125rem',
                            color: 'text.primary',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {chat.userName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', flexShrink: 0, fontSize: '0.625rem' }}
                        >
                          {formatListTime(chat.lastMessageAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625, mt: 0.25 }}>
                        <Typography
                          noWrap
                          variant="body2"
                          sx={{
                            color: chat.unreadCount ? 'text.secondary' : 'text.disabled',
                            fontWeight: chat.unreadCount ? 500 : 400,
                            fontSize: '0.75rem',
                          }}
                        >
                          {chat.lastMessagePreview}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {queueMeta && queueMeta.value !== 'all' ? (
                            <Chip
                              label={queueMeta.shortLabel}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.5625rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                color: queueMeta.color,
                                bgcolor: queueMeta.bgcolor,
                                borderRadius: '6px',
                                '& .MuiChip-label': { px: 0.625 },
                              }}
                            />
                          ) : null}
                          {chat.ariaFlagged ? (
                            <Chip
                              icon={<FlagOutlinedIcon sx={{ fontSize: '11px !important' }} />}
                              label="Aria flagged"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.5625rem',
                                fontWeight: 700,
                                color: '#a16207',
                                bgcolor: '#fef9c3',
                                borderRadius: '6px',
                                '& .MuiChip-icon': { color: '#a16207', ml: 0.5 },
                                '& .MuiChip-label': { px: 0.625 },
                              }}
                            />
                          ) : null}
                          {statusMeta ? (
                            <Chip
                              label={statusMeta.label}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.5625rem',
                                fontWeight: 700,
                                color: statusMeta.color,
                                bgcolor: statusMeta.bgcolor,
                                borderRadius: '6px',
                                '& .MuiChip-label': { px: 0.625 },
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default AriaChatList;
